/**
 * Compact shift status + open/close control for the top bar (header), so a
 * cashier can see "expected in drawer" and open/close the till without
 * leaving whatever screen they're on. Shares the exact same query key and
 * mutations as the full `/shift` page (ShiftsPage in index.tsx), so opening
 * or closing here — or there — keeps both in sync instantly. The full page
 * (with cash movements, close notes, etc.) still exists in the sidebar for
 * anything beyond a quick glance/action.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, formatPaisa } from '@pospak/shared';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../stores/auth';

interface Shift {
  id: string;
  shopId: string;
  status: 'OPEN' | 'CLOSED' | 'REOPENED';
  openingFloatPaisa: number;
  expectedCashPaisa: number;
  variancePaisa: number;
}

export function ShiftBar() {
  const activeShopId = useAuth((s) => s.activeShopId);
  const hasPermission = useAuth((s) => s.hasPermission);
  const queryClient = useQueryClient();

  const [openPopover, setOpenPopover] = useState<'open' | 'close' | null>(null);
  const [openingFloat, setOpeningFloat] = useState('');
  const [countedCash, setCountedCash] = useState('');

  const canOpen = hasPermission(PERMISSIONS.SHIFT_OPEN);
  const canClose = hasPermission(PERMISSIONS.SHIFT_CLOSE);
  const canView = hasPermission(PERMISSIONS.SHIFT_VIEW);

  // Same query key as ShiftsPage's own useQuery — one shared cache entry.
  const { data: shift } = useQuery({
    queryKey: ['currentShift', activeShopId],
    queryFn: () => api.get<Shift | null>(`/shifts/current?shopId=${activeShopId}`),
    enabled: !!activeShopId && canView,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['currentShift', activeShopId] });

  const openingFloatPaisa = Math.round(Number(openingFloat || '0') * 100);
  const openingFloatValid = Number.isFinite(openingFloatPaisa) && openingFloatPaisa >= 0;

  const openShift = useMutation({
    mutationFn: () => api.post<Shift>('/shifts/open', { shopId: activeShopId, openingFloatPaisa }),
    onSuccess: () => {
      toast.success('Shift opened');
      setOpeningFloat('');
      setOpenPopover(null);
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not open the shift'),
  });

  const closeShift = useMutation({
    mutationFn: () =>
      api.post<Shift>(`/shifts/${shift?.id}/close`, {
        countedCashPaisa: Math.round(Number(countedCash || '0') * 100),
      }),
    onSuccess: (data) => {
      toast.success(
        data.variancePaisa === 0 ? 'Shift closed — drawer balanced' : `Shift closed — variance ${formatPaisa(data.variancePaisa)}`,
      );
      setCountedCash('');
      setOpenPopover(null);
      invalidate();
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.code === 'APPROVAL_REQUIRED') {
        toast.error('This variance needs a manager to approve before the shift can close');
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not close the shift');
      }
    },
  });

  if (!canView || !activeShopId) return null;

  return (
    <div className="relative flex items-center gap-2">
      {shift ? (
        <>
          <div className="hidden items-center gap-2 rounded-lg bg-surface2 px-3 py-1.5 text-sm sm:flex">
            <span className="text-ink3">Expected in drawer</span>
            <span className="num font-semibold">{formatPaisa(shift.expectedCashPaisa)}</span>
          </div>
          {canClose && (
            <button
              type="button"
              className="btn-ghost h-8 px-2 text-xs"
              onClick={() => setOpenPopover(openPopover === 'close' ? null : 'close')}
            >
              <Lock size={14} aria-hidden />
              Close shift
            </button>
          )}
        </>
      ) : (
        canOpen && (
          <button
            type="button"
            className="btn-primary h-8 px-3 text-xs"
            onClick={() => setOpenPopover(openPopover === 'open' ? null : 'open')}
          >
            <Unlock size={14} aria-hidden />
            Open shift
          </button>
        )
      )}

      {openPopover === 'open' && (
        <div className="absolute right-0 top-10 z-20 w-64 space-y-2 rounded-lg border border-border bg-surface p-3 shadow-card">
          <label className="block text-sm">
            <span className="text-ink2">Opening float (Rs)</span>
            <input
              className="input mt-1 w-full"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
              placeholder="0"
              autoFocus
            />
          </label>
          <button
            type="button"
            className="btn-primary w-full"
            disabled={!openingFloatValid || openShift.isPending}
            onClick={() => openShift.mutate()}
          >
            Open shift
          </button>
        </div>
      )}

      {openPopover === 'close' && shift && (
        <div className="absolute right-0 top-10 z-20 w-64 space-y-2 rounded-lg border border-border bg-surface p-3 shadow-card">
          <p className="flex items-center justify-between text-sm">
            <span className="text-ink2">Expected</span>
            <span className="num font-medium">{formatPaisa(shift.expectedCashPaisa)}</span>
          </p>
          <label className="block text-sm">
            <span className="text-ink2">Counted cash (Rs)</span>
            <input
              className="input mt-1 w-full"
              value={countedCash}
              onChange={(e) => setCountedCash(e.target.value)}
              autoFocus
            />
          </label>
          {countedCash && (
            <p className="text-xs text-ink3">
              Variance:{' '}
              <span className="num">{formatPaisa(Math.round(Number(countedCash) * 100) - shift.expectedCashPaisa)}</span>
            </p>
          )}
          <div className="flex gap-2">
            <button type="button" className="btn-ghost flex-1" onClick={() => setOpenPopover(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              disabled={!countedCash || closeShift.isPending}
              onClick={() => closeShift.mutate()}
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
